// This package is the sanctioned wrapper around @tanstack/react-virtual, so it is the one place
// allowed to import it directly (consumers are steered here by the no-restricted-imports rule).
// eslint-disable-next-line no-restricted-imports
import {
  defaultRangeExtractor as tanstackDefaultRangeExtractor,
  observeWindowRect as tanstackObserveWindowRect,
  type PartialKeys,
  type Range as TanstackRange,
  type ReactVirtualizer,
  type ReactVirtualizerOptions,
  type ScrollToOptions as TanstackScrollToOptions,
  useVirtualizer as useTanstackVirtualizer,
  useWindowVirtualizer as useTanstackWindowVirtualizer,
  type VirtualItem as TanstackVirtualItem,
} from '@tanstack/react-virtual'

// Re-exported as type aliases (not `export ... from`) so consumers never import
// from `@tanstack/react-virtual` directly, without tripping the no-barrel-files rule.
export type VirtualItem = TanstackVirtualItem
export type Range = TanstackRange
export type ScrollToOptions = TanstackScrollToOptions

/** Re-export of tanstack's `defaultRangeExtractor` so consumers never import it directly. */
export const defaultRangeExtractor = tanstackDefaultRangeExtractor

/** TanStack's default window observer for consumers that adjust viewport measurements. */
export const observeWindowRect = tanstackObserveWindowRect

/**
 * The raw virtualizer instance type, re-exported for the rare consumer that needs
 * the escape hatch (see {@link VirtualizerView.virtualizer}).
 */
export type Virtualizer<TScrollElement extends Element | Window, TItemElement extends Element> = ReactVirtualizer<
  TScrollElement,
  TItemElement
>

/**
 * The plain, memoization-safe surface of a single virtualized axis. Its data
 * fields and stable callbacks are safe to read during render, because the parts
 * that are NOT memoizable — reading raw virtualizer state such as
 * `getVirtualItems()` / `getTotalSize()` during render, which makes the React
 * Compiler bail out — stay contained inside this package.
 *
 * Everything here describes the axis's own main axis: for a vertical axis
 * `start` / `size` / `totalSize` are heights, for a horizontal one they are widths.
 */
export interface VirtualizerAxisView<TScrollElement extends Element | Window, TItemElement extends Element> {
  /** Items currently inside the virtual window (plain data, incl. `start`). */
  items: VirtualItem[]
  /**
   * Total main-axis size of all items. Use it for the sizing container's main-axis
   * size unless you opt into `directDomUpdates` (which writes the size for you).
   */
  totalSize: number
  /** `scrollMargin` echoed back for sticky-header / offset math. */
  scrollMargin: number
  /**
   * Ref for the inner sizing container. Only meaningful with `directDomUpdates`,
   * where the virtualizer writes the container's main-axis size directly to the
   * DOM (so the container must NOT set `height` / `width` in its style).
   */
  containerRef: ReactVirtualizer<TScrollElement, TItemElement>['containerRef']
  /**
   * Ref for each item element. Registers the element for measurement and, when
   * `directDomUpdates` is enabled, for direct DOM positioning.
   */
  measureElement: ReactVirtualizer<TScrollElement, TItemElement>['measureElement']
  scrollToIndex: ReactVirtualizer<TScrollElement, TItemElement>['scrollToIndex']
  scrollToOffset: ReactVirtualizer<TScrollElement, TItemElement>['scrollToOffset']
  /** Imperatively re-measure all measured elements (rarely needed). */
  measure: ReactVirtualizer<TScrollElement, TItemElement>['measure']
}

/**
 * A single-axis {@link VirtualizerAxisView} plus the viewport rect and the raw
 * instance escape hatch. Consuming render paths remain fully compilable only as
 * long as they read the plain fields and avoid the escape hatch during render.
 */
export interface VirtualizerView<
  TScrollElement extends Element | Window,
  TItemElement extends Element,
> extends VirtualizerAxisView<TScrollElement, TItemElement> {
  /** Size of the scroll viewport, or `null` before first measure. */
  scrollRect: {width: number; height: number} | null
  /**
   * ESCAPE HATCH — the raw virtualizer instance. Use ONLY inside effects and
   * event handlers (imperative scroll, live `scrollOffset` reads that must not
   * trigger a re-render). NEVER read its state during render (`getVirtualItems()`,
   * `getTotalSize()`, …) — that reintroduces the compiler bail-out this package
   * exists to contain. Prefer the plain fields above wherever possible.
   */
  virtualizer: Virtualizer<TScrollElement, TItemElement>
}

function toAxisView<TScrollElement extends Element | Window, TItemElement extends Element>(
  virtualizer: ReactVirtualizer<TScrollElement, TItemElement>,
): VirtualizerAxisView<TScrollElement, TItemElement> {
  return {
    items: virtualizer.getVirtualItems(),
    totalSize: virtualizer.getTotalSize(),
    scrollMargin: virtualizer.options.scrollMargin,
    containerRef: virtualizer.containerRef,
    measureElement: virtualizer.measureElement,
    scrollToIndex: virtualizer.scrollToIndex,
    scrollToOffset: virtualizer.scrollToOffset,
    measure: virtualizer.measure,
  }
}

function toView<TScrollElement extends Element | Window, TItemElement extends Element>(
  virtualizer: ReactVirtualizer<TScrollElement, TItemElement>,
): VirtualizerView<TScrollElement, TItemElement> {
  return {
    ...toAxisView(virtualizer),
    scrollRect: virtualizer.scrollRect,
    virtualizer,
  }
}

// Options mirror `@tanstack/react-virtual` exactly. `directDomUpdates` is NOT
// forced on — it stays an opt-in option (alongside `directDomUpdatesMode`) that a
// consumer passes once a list is ready for direct DOM positioning. This package's
// job is the compiler-safe `VirtualizerView`, not enabling directDomUpdates.
type ElementVirtualizerOptions<TScrollElement extends Element, TItemElement extends Element> = PartialKeys<
  ReactVirtualizerOptions<TScrollElement, TItemElement>,
  'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
>

type WindowVirtualizerOptions<TItemElement extends Element> = PartialKeys<
  ReactVirtualizerOptions<Window, TItemElement>,
  'getScrollElement' | 'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
>

/**
 * Compiler-safe wrapper around `@tanstack/react-virtual`'s `useVirtualizer`.
 * Returns a {@link VirtualizerView} of plain values and stable callbacks instead
 * of the mutable virtualizer instance, so the React Compiler bail-out stays
 * contained here.
 */
export function useVirtualizer<TScrollElement extends Element, TItemElement extends Element>(
  options: ElementVirtualizerOptions<TScrollElement, TItemElement>,
): VirtualizerView<TScrollElement, TItemElement> {
  'use no memo' // react virtualizer is unsupported, so we pull the behavior here to avoid the compiler bail-out. This is a temporary workaround until support exists in the virtualization library.
  const virtualizer = useTanstackVirtualizer<TScrollElement, TItemElement>(options)
  return toView(virtualizer)
}

/**
 * Compiler-safe wrapper around `@tanstack/react-virtual`'s `useWindowVirtualizer`.
 * Returns a {@link VirtualizerView} of plain values and stable callbacks instead
 * of the mutable virtualizer instance, so the React Compiler bail-out stays
 * contained here.
 */
export function useWindowVirtualizer<TItemElement extends Element>(
  options: WindowVirtualizerOptions<TItemElement>,
): VirtualizerView<Window, TItemElement> {
  'use no memo' // react virtualizer is unsupported, so we pull the behavior here to avoid the compiler bail-out. This is a temporary workaround until support exists in the virtualization library.
  const virtualizer = useTanstackWindowVirtualizer<TItemElement>(options)
  return toView(virtualizer)
}
/**
 * Per-axis options for {@link useVirtualizer2D}. Identical to the single-axis
 * options minus the two things the wrapper owns: `getScrollElement` (both axes
 * share one scroll container) and `horizontal` (set per axis by the wrapper).
 */
export type Virtualizer2DAxisOptions<TScrollElement extends Element, TItemElement extends Element> = Omit<
  ElementVirtualizerOptions<TScrollElement, TItemElement>,
  'getScrollElement' | 'horizontal'
>

/**
 * Options for {@link useVirtualizer2D}. The two axes carry their own item element
 * type, because each axis measures its own elements: a grid can measure columns on
 * header cells and rows on row elements without widening either axis to a union.
 */
export interface Virtualizer2DOptions<
  TScrollElement extends Element,
  THorizontalItemElement extends Element,
  TVerticalItemElement extends Element,
> {
  /** The single scroll container both axes observe. */
  getScrollElement: () => TScrollElement | null
  horizontal: Virtualizer2DAxisOptions<TScrollElement, THorizontalItemElement>
  vertical: Virtualizer2DAxisOptions<TScrollElement, TVerticalItemElement>
}

/**
 * A two-dimensional virtual window over one scroll container: one axis view per
 * direction, each with its own items (offset + size), total extent and scroll
 * callbacks.
 *
 * Deliberately has NO raw virtualizer escape hatch. Two dimensions is two
 * instances composed, and handing either one out would move the React Compiler
 * bail-out back into the caller — the thing this package exists to contain.
 */
export interface Virtualizer2DView<
  TScrollElement extends Element,
  THorizontalItemElement extends Element,
  TVerticalItemElement extends Element,
> {
  /** The horizontal axis: `start` / `size` / `totalSize` are widths, driven by `scrollLeft`. */
  horizontal: VirtualizerAxisView<TScrollElement, THorizontalItemElement>
  /** The vertical axis: `start` / `size` / `totalSize` are heights, driven by `scrollTop`. */
  vertical: VirtualizerAxisView<TScrollElement, TVerticalItemElement>
  /**
   * Size of the shared scroll viewport, or `null` before first measure. Both
   * axes observe the same element, so this is stated once; the fallback covers
   * an axis turned off with `enabled: false`, which never measures.
   */
  scrollRect: {width: number; height: number} | null
}

/**
 * Compiler-safe two-dimensional wrapper around `@tanstack/react-virtual`.
 * Composes a horizontal and a vertical virtualizer over one scroll container and
 * returns a {@link Virtualizer2DView} of plain values and stable callbacks, so
 * the composition — and the compiler bail-out it would otherwise cause — stays
 * contained here.
 *
 * Each axis measures its own elements, so give each axis the elements laid out along
 * it — and each axis carries its own item element type, so a grid measuring columns on
 * header cells and rows on row elements keeps both `measureElement` callbacks precisely
 * typed. Two instances sharing one scroll container do constrain what a caller can do
 * with a *single* element or a single scroll:
 *
 * - Both axes read an element's index from the same `data-index` attribute, so an
 *   element measured on both must give each axis its own `indexAttribute`.
 * - `directDomUpdates` in the default `'transform'` mode writes the whole
 *   `transform` property, so an element measured on both axes loses one axis'
 *   offset. Use `directDomUpdatesMode: 'position'` (which writes `left` vs `top`
 *   and composes) or keep each axis on its own elements. Each axis also has its
 *   own `containerRef`, so a shared sizing container needs both refs merged onto it.
 * - Each axis scrolls the shared container on its own, and a second `scrollTo`
 *   aborts an in-flight smooth scroll on the same scrolling box. Scrolling both
 *   axes at once therefore needs `behavior: 'instant'`; only one axis at a time
 *   can scroll smoothly.
 */
export function useVirtualizer2D<
  TScrollElement extends Element,
  THorizontalItemElement extends Element,
  TVerticalItemElement extends Element,
>(
  options: Virtualizer2DOptions<TScrollElement, THorizontalItemElement, TVerticalItemElement>,
): Virtualizer2DView<TScrollElement, THorizontalItemElement, TVerticalItemElement> {
  'use no memo' // react virtualizer is unsupported, so we pull the behavior here to avoid the compiler bail-out. This is a temporary workaround until support exists in the virtualization library.
  const horizontalVirtualizer = useTanstackVirtualizer<TScrollElement, THorizontalItemElement>({
    ...options.horizontal,
    getScrollElement: options.getScrollElement,
    horizontal: true,
  })
  const verticalVirtualizer = useTanstackVirtualizer<TScrollElement, TVerticalItemElement>({
    ...options.vertical,
    getScrollElement: options.getScrollElement,
    horizontal: false,
  })

  return {
    horizontal: toAxisView(horizontalVirtualizer),
    vertical: toAxisView(verticalVirtualizer),
    scrollRect: verticalVirtualizer.scrollRect ?? horizontalVirtualizer.scrollRect,
  }
}
